import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';
import withTranslations from '@wheelbox/lithejs-i18n';

import { SectionHeading, CodeBlock } from '@components/shared';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@wheelbox/lithejs-ui';

export interface CodeExamplesSectionProps {
  id?: string;
  testId?: string;
}

const codeExamples = {
  component: `import { LitheFC, LitheElement } from 'lithejs-engine';

const Greeting: LitheFC<{ name: string }> = ({ name }) => {
  return [
    'div',
    { class: 'greeting' },
    ['h1', {}, \`Hello, \${name}!\`],
    ['p', {}, 'Welcome to LitheJS'],
  ];
};

// Usage
Greeting({ name: 'World' });`,

  state: `import createState from 'lithejs-state';

const state = createState();

// Create a slice
state.createSlice('counter', { count: 0 });

// Subscribe to changes
const Counter = () => {
  const { count } = state.withSlice('counter');
  
  const increment = () => {
    state.updateSlice('counter', { count: count + 1 });
  };

  return ['button', { onclick: increment }, \`Count: \${count}\`];
};`,

  routing: `import { createRouter, goto, Link } from 'lithejs-router';

const routes = [
  { path: '/', component: HomePage },
  { path: '/about', component: AboutPage },
  { path: '/user/[id]', component: UserPage },
];

createRouter({ routes, root: '#app' });

// Navigation
Link({ href: '/about' }, 'Go to About');
goto('/user/123');`,
};

const CodeExamplesSection: LitheFC<CodeExamplesSectionProps> = ({
  id = 'code-examples-section',
  testId = 'code-examples-section',
}): LitheElement<'section'> => {
  const t = withTranslations('Home');

  const tabs = ['component', 'state', 'routing'] as const;

  return [
    'section',
    {
      id,
      'data-testid': testId,
      class: 'py-20 px-4',
    },
    [
      'div',
      { class: 'max-w-4xl mx-auto' },
      SectionHeading({
        id: `${id}-heading`,
        title: t('codeExamples.title'),
        subtitle: t('codeExamples.subtitle'),
      }),
      Tabs(
        { defaultValue: 'component' },
        TabsList(
          { classes: 'grid w-full grid-cols-3 mb-6' },
          ...tabs.map((tab) =>
            TabsTrigger({ value: tab }, t(`codeExamples.tabs.${tab}`))
          )
        ),
        ...tabs.map((tab) =>
          TabsContent(
            { value: tab },
            CodeBlock({
              id: `${id}-code-${tab}`,
              code: codeExamples[tab],
              language: 'typescript',
              filename: `${tab}.ts`,
              showLineNumbers: true,
            })
          )
        )
      ),
    ],
  ];
};

export default CodeExamplesSection;
